import React from 'react'
import { Form, Field, yupToFormErrors } from 'formik'
import { object, string, boolean } from 'yup'
import PropTypes from 'prop-types'
import cx from 'classnames'
import _get from 'lodash/get'
import { useAuth } from '../AuthProvider/AuthProvider'
import { useNotInDeliveryAreaWarning } from '../NotInDeliveryAreaWarning/use-not-in-delivery-area-warning'
import { useOutpost } from '../OutpostProvider/OutpostProvider'

import { ApprovalRules } from '../Cart/ApprovalRules/ApprovalRules'
import CartTable from '../Cart/CartTable/CartTable'
import CartFooter from '../Cart/CartFooter/CartFooter'
import Fieldset from '../Modifiers/Fieldset/Fieldset'

import {
  PaymentOptionsShape,
  getCreditPaymentOption,
  getSavedCreditPaymentOption,
  getInitialPaymentType,
  getPaymentUponReceiptOption
} from './payment-options'

import Button from '../Button/Button'
import Error from '../Error/Error'
import Progress from '../Progress/Progress'
import Radio from '../Form/Radio/Radio'
import { Password } from '../Form/Password/Password'
import * as CustomerInfo from './CustomerInfo/CustomerInfo'
import * as CurbsidePickup from './CurbsidePickup/CurbsidePickup'
import * as CheckoutDeliveryInfo from './CheckoutDeliveryInfo/CheckoutDeliveryInfo'
import * as GiftCard from './GiftCard/GiftCard'
import * as DeliveryCommunicationConsent from './DeliveryCommunicationConsent/DeliveryCommunicationConsent'
import * as Tip from './Tip/Tip'
import { CreditCard } from '../CreditCard/CreditCard'
import { passwordSchema } from '../../utils/form-schemas'
import { CheckoutSavedCreditCard } from '../CheckoutSavedCreditCard/CheckoutSavedCreditCard'
import { SaveNewCardCheckbox } from '../SaveNewCardCheckbox/SaveNewCardCheckbox'
import { FormikProvider } from '../FormikProvider/FormikProvider'
import { PromoCodeContainer } from '../PromoCodeContainer/PromoCodeContainer'
import { getPrimaryCreditCard } from '../../utils/form-utils'

import { useCreditCard } from '../CreditCardProvider/CreditCardProvider'
import { UnlessAuthenticated, IfAuthenticated } from '../IfAuthenticated/IfAuthenticated'
import { FlagOn, useFlag } from '../FeatureFlag/FeatureFlag'
import { LoyaltyRedemption } from '../Loyalty/LoyaltyRedemption/LoyaltyRedemption'
import { useFulfillment } from '../FulfillmentProvider/FulfillmentProvider'
import { useGiftCard } from '../GiftCardProvider/GiftCardProvider'
import { useCurbsidePickup } from '../use-curbside-pickup/use-curbside-pickup'
import { useSavedAddresses } from '../use-saved-addresses/use-saved-addresses'
import { hasSufficientFunds } from '../../utils/gift-card-helpers'
import { getFlagsLoading } from '../../utils/checkout-helpers'

import './CheckoutForm.css'
import { CheckoutError } from '../CheckoutError/CheckoutError'
import { useServiceAvailability, SERVICE_AVAILABILITY, SERVICES } from '../ServiceAvailabilityProvider/ServiceAvaialbilityProvider'
import { useRestaurant } from '../RestaurantProvider/RestaurantProvider'
import { PlaceOrderDegradedMessage } from './PlaceOrderDegradedMessage/PlaceOrderDegradedMessage'
import { SmsCommunicationConsent } from './SmsCommunicationConsent/SmsCommunicationConsent'
import { AccountWarning } from './AccountWarning/AccountWarning'

const formModules = [
  CustomerInfo,
  CheckoutDeliveryInfo,
  CurbsidePickup,
  GiftCard,
  DeliveryCommunicationConsent,
  Tip
]

const whenNewCredit = ({ then, otherwise = string().default('') }) =>
  string().when(['$giftCardHasSufficientFunds', 'paymentType', 'creditCardTip'], {
    is: (giftCardHasSufficientFunds, paymentType, creditCardTip) => {
      if (paymentType !== 'CREDIT_CARD') return false
      if (creditCardTip > 0) return true
      return !giftCardHasSufficientFunds
    },
    then,
    otherwise
  })

const whenSavedCredit = ({ then, otherwise = string().default('') }) =>
  string().when(['$giftCardHasSufficientFunds', 'paymentType', 'creditCardTip'], {
    is: (giftCardHasSufficientFunds, paymentType, creditCardTip) => {
      if (paymentType !== 'SAVED_CREDIT_CARD') return false
      if (creditCardTip > 0) return true
      return !giftCardHasSufficientFunds
    },
    then,
    otherwise
  })

const getValidationSchema = ({
  cart,
  curbsidePickupConfig,
  featureFlags,
  fulfillmentContext
}) => {
  // To be refactored into their own form modules.
  const legacyValidationSchema = {
    paymentType: string()
      .trim()
      .required('required')
      .when('$outpostInfo.isOutpost', {
        is: true,
        then: string()
          .oneOf(
            ['SAVED_CREDIT_CARD', 'CREDIT_CARD'],
            'must be a valid payment type'
          ),
        otherwise: string()
          .oneOf(
            ['CASH', 'SAVED_CREDIT_CARD', 'CREDIT_CARD'],
            'must be a valid payment type'
          )
      }),
    savedCardGuid: whenSavedCredit({
      then: string()
        .required('required')
        // guid will be prefixed with '!' if card isn't accepted.
        .matches(/^[^!]/, 'must be an accepted card type')
    }),
    encryptedCard: whenNewCredit({
      // The validation only seems to work with string() even though this value is
      // actually an object. Note that the value itself should be opaque since it
      // came from the iframe so we really only care whether it is present or not.
      then: string().required('required')
    }),
    saveCard: boolean()
      .default(false),
    password: string()
      .when(['$authenticated', 'paymentType', 'saveCard'], {
        is: (authenticated, paymentType, saveCard) => (
          !authenticated && paymentType === 'CREDIT_CARD' && saveCard
        ),
        then: passwordSchema,
        otherwise: string().default('')
      })
  }

  const validationSchema = formModules.reduce((acc, formModule) => ({
    ...acc,
    ...(
      formModule.getValidationSchema
        ? formModule.getValidationSchema({
          cart,
          curbsidePickupConfig,
          featureFlags,
          fulfillmentContext
        })
        : null
    )
  }), legacyValidationSchema)

  return object().shape(validationSchema)
}

const getArgsForSubmit = ({
  cart,
  featureFlags,
  fulfillmentContext,
  giftCardContext,
  outpostInfo,
  savedAddresses,
  values,
  curbsidePickupConfig
}) => {
  const { isOutpost, diningOptionGuid } = outpostInfo
  let { paymentType } = values
  const { giftCard } = giftCardContext

  const customer = CustomerInfo.getArgsForSubmit({ values })

  // TODO Refactor to leverage the `formModules` array like getInitialValues().
  const deliveryInfoArgs = CheckoutDeliveryInfo.getArgsForSubmit({
    featureFlags,
    fulfillmentContext,
    savedAddresses,
    values
  })

  const curbsideArgs = CurbsidePickup.getArgsForSubmit({
    featureFlags,
    fulfillmentContext,
    values,
    curbsidePickupConfig
  })

  const giftCardArgs = GiftCard.getArgsForSubmit({
    featureFlags,
    giftCardContext
  })

  const deliveryCommunicationConsentArgs = DeliveryCommunicationConsent.getArgsForSubmit({
    values,
    cart,
    featureFlags
  })

  const tipArgs = Tip.getArgsForSubmit({
    values,
    cart
  })

  if (hasSufficientFunds({ giftCard, cart }) && tipArgs.tipAmount === 0) {
    return {
      cashInput: {
        ...customer,
        ...deliveryInfoArgs,
        ...curbsideArgs,
        ...giftCardArgs
      },
      paymentType: 'CASH'
    }
  }

  if (paymentType === 'SAVED_CREDIT_CARD' || paymentType === 'CREDIT_CARD') {
    // TODO rename CREDIT_CARD to CREDIT everywhere (the constant the backend uses)

    const argsForSubmit = {
      ccInput: {
        ...customer,
        ...deliveryCommunicationConsentArgs,
        ...deliveryInfoArgs,
        ...curbsideArgs,
        ...giftCardArgs,
        ...tipArgs
      },
      paymentType: 'CREDIT'
    }

    if (isOutpost) {
      argsForSubmit.ccInput.diningOptionGuid = diningOptionGuid
    }

    if (paymentType === 'SAVED_CREDIT_CARD') {
      argsForSubmit.ccInput.savedCardInput = {
        cardGuid: values.savedCardGuid
      }
    } else {
      argsForSubmit.ccInput.newCardInput = {
        ...values.encryptedCard,
        saveCard: values.saveCard
      }

      if (values.saveCard) {
        argsForSubmit.password = values.password
      }
    }

    return argsForSubmit
  }

  return {
    cashInput: {
      ...customer,
      ...deliveryInfoArgs,
      ...curbsideArgs,
      ...giftCardArgs
    },
    paymentType: 'CASH'
  }
}

const getDefaultCard = (creditCards = []) => {
  const card = getPrimaryCreditCard(creditCards)
  return card.guid || ''
}

const getInitialValues = ({
  cart,
  creditCards,
  featureFlags,
  fulfillmentContext,
  giftCardContext,
  paymentOptions,
  savedAddresses,
  user,
  curbsidePickupConfig
}) => {
  // To be refactored into their own form modules.
  const legacyInitialValues = {
    savedCardGuid: getDefaultCard(creditCards),
    encryptedCard: '',
    password: '',
    paymentType: getInitialPaymentType(paymentOptions, creditCards),
    saveCard: false
  }

  return formModules.reduce((acc, formModule) => ({
    ...acc,
    ...(
      formModule.getInitialValues
        ? formModule.getInitialValues({
          cart,
          user,
          featureFlags,
          fulfillmentContext,
          giftCardContext,
          savedAddresses,
          curbsidePickupConfig
        })
        : null
    ),
    gcNumber: ''
  }), legacyInitialValues)
}

const CheckoutForm = ({
  onSubmit,
  error,
  paymentOptions,
  cart,
  promoCodesEnabled = false
}) => {
  const giftCardContext = useGiftCard()
  const { giftCard, clearGiftCard } = giftCardContext
  const outpostInfo = useOutpost()
  const {
    curbsidePickupConfig,
    loading: csLoading,
    error: csError,
    refetch: refetchCS
  } = useCurbsidePickup()
  const fulfillmentContext = useFulfillment()
  const {
    creditCards,
    loading: ccLoading
  } = useCreditCard()
  const savedAddressesContext = useSavedAddresses()
  const {
    savedAddresses,
    loading: savedAddressesLoading,
    error: savedAddressesError,
    refetch: refetchSavedAddresses
  } = savedAddressesContext
  let {
    authenticated,
    user,
    loading: authLoading
  } = useAuth()
  user = user || {}
  const { creditCardConfig = {}, loyaltyConfig = {} } = cart.restaurant

  const { restaurantInfo } = useRestaurant()
  const restaurantPhone = React.useMemo(() => restaurantInfo && _get(restaurantInfo, 'data.restaurant.location.phone'), [restaurantInfo])

  const featureFlags = {
    'oo-daas-integration': useFlag('oo-daas-integration', false),
    'oo-v3-delivery': useFlag('oo-v3-delivery', false),
    'oo-v3-canned-delivery-instructions': useFlag('oo-v3-canned-delivery-instructions', false),
    'oo-v3-curbside-pickup-v2': useFlag('oo-v3-curbside-pickup-v2', false),
    'oo-v3-gift-card-redemption': useFlag('oo-v3-gift-card-redemption', false),
    'oo-v3-include-default-tip': useFlag('oo-v3-include-default-tip', false),
    'oo-v3-use-service-availability': useFlag('oo-v3-use-service-availability', false)
  }

  const { hasWarning: hasNotInDeliveryAreaWarning } = useNotInDeliveryAreaWarning()

  const { [SERVICES.PLACE_ORDER]: placeOrderService } = useServiceAvailability()

  const isPlaceOrderDegraded = React.useMemo(() => (
    (placeOrderService && placeOrderService.status === SERVICE_AVAILABILITY.DEGRADED)
  ), [placeOrderService])

  const flagsLoading = getFlagsLoading(featureFlags)

  if (fulfillmentContext.loading ||
    authLoading ||
    ccLoading ||
    savedAddressesLoading ||
    flagsLoading ||
    csLoading
  ) {
    return <Progress eventData={{ category: 'Checkout Form' }} />
  }

  if (fulfillmentContext.error) {
    return <Error error={fulfillmentContext.error} />
  }
  if (csError) {
    return <Error error={csError} retry={refetchCS} />
  }
  if (savedAddressesError) {
    return <Error error={savedAddressesError} retry={refetchSavedAddresses} />
  }

  const initialValues = getInitialValues({
    cart,
    creditCards,
    featureFlags,
    fulfillmentContext,
    giftCardContext,
    paymentOptions,
    savedAddresses,
    user,
    curbsidePickupConfig
  })
  const validationSchema = getValidationSchema({
    cart,
    curbsidePickupConfig,
    featureFlags,
    fulfillmentContext,
    giftCardContext
  })
  let isInitialValid = validationSchema.isValidSync(initialValues, {
    context: {
      authenticated,
      featureFlags,
      fulfillmentContext,
      giftCardContext,
      outpostInfo,
      giftCardHasSufficientFunds: hasSufficientFunds({ cart, giftCard })
    }
  })

  // See this regarding custom validate prop instead of validationSchema so
  // that we can pass custom context to yup.
  // https://github.com/jaredpalmer/formik/issues/1359
  return (
    <FormikProvider
      initialValues={initialValues}
      isInitialValid={isInitialValid}
      validate={values => validationSchema
        .validate(values, {
          abortEarly: false,
          context: {
            authenticated,
            featureFlags,
            fulfillmentContext,
            giftCardContext,
            outpostInfo,
            giftCardHasSufficientFunds: hasSufficientFunds({ cart, giftCard })
          }
        })
        .catch(err => {
          throw yupToFormErrors(err)
        })
      }
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const submitArgs = getArgsForSubmit({
            cart,
            featureFlags,
            fulfillmentContext,
            giftCardContext,
            outpostInfo,
            values,
            savedAddresses,
            curbsidePickupConfig
          })
          await onSubmit(submitArgs)
        } catch (e) {
          console.warn('onSubmit', e)
        } finally {
          clearGiftCard()
          setSubmitting(false)
        }
      }}
    >
      {({
        values,
        handleChange,
        isSubmitting,
        isValid,
        handleSubmit,
        setFieldValue,
        validateForm
      }) => {
        const creditOption = getCreditPaymentOption({ authenticated, paymentOptions })
        const savedCreditOption =
          creditCards && getSavedCreditPaymentOption({ authenticated, paymentOptions })
        // TODO: once outpost is part of the cart the backend can then determine paymentOptions considering outpost
        const paymentUponReceiptOption = !outpostInfo.isOutpost && getPaymentUponReceiptOption({ paymentOptions })
        const optionMap = {
          SAVED_CREDIT_CARD: savedCreditOption,
          CREDIT_CARD: creditOption,
          CASH: paymentUponReceiptOption
        }
        const selectedOption = optionMap[values.paymentType]
        const canShowTip =
          (values.paymentType === 'SAVED_CREDIT_CARD' ||
            values.paymentType === 'CREDIT_CARD') &&
          creditCardConfig.tipEnabled
        const { loyaltyRedemptionEnabled } = loyaltyConfig
        return (
          <Form styleName='form' onSubmit={handleSubmit}>
            <AccountWarning />
            <Fieldset label='Your Information'>
              <CustomerInfo.Component />
              <DeliveryCommunicationConsent.Component cart={cart} />
              <SmsCommunicationConsent cart={cart} />
            </Fieldset>
            <CurbsidePickup.Component curbsidePickupConfig={curbsidePickupConfig} />
            <CheckoutDeliveryInfo.Component savedAddresses={savedAddresses} />
            <Fieldset
              label='Payment Method'
              collapsable
              id='payment_type_fields'
            >
              <GiftCard.Component cart={cart} validateForm={validateForm} />
              <div styleName='radio-tabs'>
                {Boolean(savedCreditOption) && (
                  <Field
                    data-testid='payment-type-saved-CC'
                    style='tab'
                    component={Radio}
                    name='paymentType'
                    id='payment_type_saved_cc'
                    label={savedCreditOption.displayName}
                    value='SAVED_CREDIT_CARD'
                  />
                )}
                {Boolean(creditOption) && (
                  <Field
                    data-testid='payment-type-CC'
                    style='tab'
                    component={Radio}
                    name='paymentType'
                    id='payment_type_cc'
                    label={creditOption.displayName}
                    value='CREDIT_CARD'
                  />
                )}
                {Boolean(paymentUponReceiptOption) && (
                  <Field
                    data-testid='payment-type-cash'
                    style='tab'
                    component={Radio}
                    name='paymentType'
                    id='payment_type_cash'
                    label={paymentUponReceiptOption.displayName}
                    value='CASH'
                    onChange={handleChange}
                  />
                )}
              </div>
              {!(
                savedCreditOption ||
                creditOption ||
                paymentUponReceiptOption
              ) && (
                <p styleName='description'>
                  The restaurant is currently not accepting orders.
                </p>
              )}
              {selectedOption &&
                selectedOption.description && (
                <p styleName='description'>{selectedOption.description}</p>
              )}
              {values.paymentType === 'SAVED_CREDIT_CARD' && (
                <Field
                  amexAccepted={creditCardConfig.amexAccepted}
                  component={CheckoutSavedCreditCard}
                  name='savedCardGuid'
                />
              )}
              <div
                styleName={cx({ hidden: values.paymentType !== 'CREDIT_CARD' })}
              >
                <Field
                  component={CreditCard}
                  name='encryptedCard'
                  amexAccepted={creditCardConfig.amexAccepted}
                />
                <Field
                  component={SaveNewCardCheckbox}
                  name='saveCard'
                  disabled={!values.encryptedCard}
                  onChange={(event) => {
                    handleChange(event)
                    setFieldValue('password', '')
                  }}
                />
                <UnlessAuthenticated>
                  <div styleName={cx({ hidden: values.saveCard === false })}>
                    <Field
                      component={Password}
                      label='Password'
                      name='password'
                      id='checkout_password'
                      value={values.password}
                      autoComplete='off'
                    />
                  </div>
                </UnlessAuthenticated>
              </div>
            </Fieldset>
            {cart ? (
              <Fieldset
                label={`Your Order (${cart.order.numberOfSelections})`}
                collapsable
                collapsed
              >
                <CartTable cart={cart} />
              </Fieldset>
            ) : null}
            {loyaltyRedemptionEnabled &&
              <FlagOn name='oo-v3-loyalty'>
                <IfAuthenticated>
                  <LoyaltyRedemption cart={cart} />
                </IfAuthenticated>
              </FlagOn>
            }
            {promoCodesEnabled && (
              <Fieldset
                label='Promo Code'
                collapsable
              >
                <FormikProvider initialValues={{ promoCode: '' }}>
                  <PromoCodeContainer cart={cart} />
                </FormikProvider>
              </Fieldset>
            )}
            {canShowTip && (<Tip.Component />)}
            {cart ? (
              <CartFooter
                cart={cart}
                showTotal
                showTip={canShowTip}
                tip={(canShowTip && parseFloat(values.creditCardTip)) || null}
              />
            ) : null}
            {cart ? (
              <ApprovalRules cart={cart} />
            ) : null}
            <p styleName='legal' data-testid='legal-links'>
              The information you provide will be processed pursuant to Toast's{' '}
              <a href='https://pos.toasttab.com/terms-of-service/#diner-tos' rel='noopener noreferrer' target='_blank' data-testid='link-terms-of-service'>
                Diner Terms of Service
              </a>{' '}
              and{' '}
              <a href='https://pos.toasttab.com/privacy' rel='noopener noreferrer' target='_blank' data-testid='link-privacy'>
                Privacy Statement
              </a>
            </p>
            <div styleName='fieldset' style={{ paddingBottom: 0 }}>
              <CheckoutError loading={isSubmitting} error={error} onSubmit={handleSubmit} />
            </div>
            <div styleName='wrapper'>
              <Button
                data-testid='submit-button'
                id='submit-button'
                eventData={{
                  category: 'Checkout',
                  action: 'Place Order'
                }}
                style='primary'
                type='submit'
                loading={isSubmitting}
                disabled={
                  !isValid ||
                  fulfillmentContext.updating ||
                  hasNotInDeliveryAreaWarning
                }
                responsive
              >
                Submit
              </Button>
            </div>
            {isPlaceOrderDegraded && (
              <PlaceOrderDegradedMessage restaurantPhone={restaurantPhone} />
            )}
          </Form>
        )
      }}
    </FormikProvider>
  )
}

CheckoutForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.object,
  paymentOptions: PaymentOptionsShape,
  cart: PropTypes.object.isRequired,
  promoCodesEnabled: PropTypes.bool
}

export default CheckoutForm
